import React, { useState } from 'react';
import { 
  BarChart, 
  TrendingUp, 
  Download, 
  Target,
  Users,
  Filter,
  Zap,
  ChevronDown,
  ChevronRight,
  Calendar,
  ArrowRight,
  Play,
  CircleDashed,
  AlertCircle,
  ThumbsUp,
  Edit3
} from 'lucide-react';

export default function MediaPlanningTable() {
  const [expandedObjectives, setExpandedObjectives] = useState([]);
  const [expandedPlatforms, setExpandedPlatforms] = useState([]);
  
  const toggleObjective = (id) => {
    if (expandedObjectives.includes(id)) {
      setExpandedObjectives(expandedObjectives.filter(item => item !== id));
    } else {
      setExpandedObjectives([...expandedObjectives, id]);
    }
  };
  
  const togglePlatform = (id) => {
    if (expandedPlatforms.includes(id)) {
      setExpandedPlatforms(expandedPlatforms.filter(item => item !== id));
    } else {
      setExpandedPlatforms([...expandedPlatforms, id]);
    }
  };
  
  // Kampanya Hedefleri
  const objectives = [
    {
      id: 'brand_awareness',
      name: 'Brand Awareness',
      description: 'Increase brand recognition and recall',
      kpi: 'Reach, Impressions, Video Views',
      platforms: ['meta', 'google', 'tiktok', 'youtube', 'pinterest', 'dv360'],
      color: 'bg-blue-100 text-blue-700 border-blue-200',
      icon: <TrendingUp className="w-4 h-4" />
    },
    {
      id: 'consideration',
      name: 'Consideration',
      description: 'Drive product interest and engagement',
      kpi: 'Engagement Rate, Click-through Rate, Video Completion Rate',
      platforms: ['meta', 'google', 'youtube', 'dv360'],
      color: 'bg-purple-100 text-purple-700 border-purple-200',
      icon: <ThumbsUp className="w-4 h-4" />
    },
    {
      id: 'conversion',
      name: 'Conversion',
      description: 'Generate leads and sales',
      kpi: 'Conversion Rate, Cost per Acquisition, ROAS',
      platforms: ['meta', 'google', 'dv360'],
      color: 'bg-green-100 text-green-700 border-green-200',
      icon: <Target className="w-4 h-4" />
    }
  ];

  // Platform Detayları
  const platforms = [
    {
      id: 'meta',
      name: 'Meta Ads',
      logo: '/meta-logo.png',
      formats: [
        { id: 'engagement', name: 'Engagement', icon: <TrendingUp className="w-3 h-3" /> },
        { id: 'reach', name: 'Reach', icon: <Users className="w-3 h-3" /> },
        { id: 'video_views', name: 'Video Views', icon: <Play className="w-3 h-3" /> }
      ],
      audiences: [
        { id: 'interest', name: 'Interest' },
        { id: 'lookalike', name: 'Lookalike' }
      ],
      color: 'from-blue-400 to-indigo-500',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      budget: 122000,
      reach: 12500000,
      frequency: 2.8,
      cpm: 9.76,
      videoViews: 240000
    },
    {
      id: 'google',
      name: 'Google Ads',
      logo: '/google-ads-logo.png',
      formats: [
        { id: 'display', name: 'Display', icon: <AlertCircle className="w-3 h-3" /> },
        { id: 'video', name: 'Video', icon: <Play className="w-3 h-3" /> }
      ],
      audiences: [
        { id: 'lookalike', name: 'Lookalike' },
        { id: 'custom_affinity', name: 'Custom Affinity' },
        { id: 'custom_intent', name: 'Custom Intent' }
      ],
      color: 'from-blue-500 to-blue-600',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      budget: 220000,
      reach: 10281690,
      frequency: 1.2,
      cpm: 21.40,
      videoViews: 2000000
    },
    {
      id: 'tiktok',
      name: 'TikTok Ads',
      logo: '/tiktok-logo.png',
      formats: [
        { id: 'reach', name: 'Reach', icon: <Users className="w-3 h-3" /> },
        { id: 'video_views', name: 'Video Views', icon: <Play className="w-3 h-3" /> }
      ],
      audiences: [
        { id: 'interest', name: 'Interest' }
      ],
      color: 'from-pink-500 to-rose-500',
      bgColor: 'bg-pink-50',
      borderColor: 'border-pink-200',
      budget: 70000,
      reach: 9915014,
      frequency: 1.5,
      cpm: 7.06,
      videoViews: 0
    },
    {
      id: 'dv360',
      name: 'DV360',
      logo: '/dv360-logo.png',
      formats: [
        { id: 'banner', name: 'Banner', icon: <AlertCircle className="w-3 h-3" /> },
        { id: 'video', name: 'Video', icon: <Play className="w-3 h-3" /> }
      ],
      audiences: [
        { id: 'custom_affinity', name: 'Custom Affinity' },
        { id: 'custom_intent', name: 'Custom Intent' },
        { id: 'interest', name: 'Interest' },
        { id: 'category', name: 'Category' },
        { id: 'url_placement', name: 'URL Placement' }
      ],
      color: 'from-green-500 to-teal-500',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
      budget: 100000,
      reach: 8333333,
      frequency: 1.6,
      cpm: 12.00,
      videoViews: 100000
    },
    {
      id: 'youtube',
      name: 'YouTube',
      logo: '/youtube-logo.png',
      formats: [
        { id: 'bumper', name: 'Bumper', icon: <Play className="w-3 h-3" /> },
        { id: 'nonskippable', name: 'Non-skippable', icon: <Play className="w-3 h-3" /> },
        { id: 'trueview', name: 'TrueView', icon: <Play className="w-3 h-3" /> }
      ],
      audiences: [
        { id: 'custom_affinity', name: 'Custom Affinity' },
        { id: 'custom_intent', name: 'Custom Intent' },
        { id: 'interest', name: 'Interest' },
        { id: 'channel_placement', name: 'Channel Placement' }
      ],
      color: 'from-red-500 to-red-600',
      bgColor: 'bg-red-50',
      borderColor: 'border-red-200',
      budget: 0,
      reach: 0,
      frequency: 0,
      cpm: 0,
      videoViews: 0
    },
    {
      id: 'pinterest',
      name: 'Pinterest',
      logo: '/pinterest-logo.png',
      formats: [
        { id: 'brand_awareness', name: 'Brand Awareness', icon: <TrendingUp className="w-3 h-3" /> }
      ],
      audiences: [
        { id: 'interest', name: 'Interest' }
      ],
      color: 'from-red-400 to-red-500',
      bgColor: 'bg-red-50',
      borderColor: 'border-red-200',
      budget: 10000,
      reach: 871080,
      frequency: 1.2,
      cpm: 11.48,
      videoViews: 0
    }
  ];
  
  // Kampanya Bilgileri
  const campaignInfo = {
    name: "Summer 2023 Branding Campaign",
    dateRange: "June 1 - August 31, 2023",
    totalBudget: 522000,
    totalReach: 41901117,
    totalImpressions: 65864754,
    avgFrequency: 1.57,
    avgCPM: 7.93,
    totalVideoViews: 2340000
  };

  // Ana Metrikler
  const mainKPIs = [
    { name: 'Budget', value: `₺${campaignInfo.totalBudget.toLocaleString()}`, icon: <BarChart />, color: 'bg-indigo-50 text-indigo-700' },
    { name: 'Reach', value: (campaignInfo.totalReach/1000000).toFixed(1) + 'M', icon: <Users />, color: 'bg-blue-50 text-blue-700' },
    { name: 'Impressions', value: (campaignInfo.totalImpressions/1000000).toFixed(1) + 'M', icon: <Eye />, color: 'bg-green-50 text-green-700' },
    { name: 'Video Views', value: (campaignInfo.totalVideoViews/1000000).toFixed(1) + 'M', icon: <Play />, color: 'bg-red-50 text-red-700' }
  ];
  
  // Timeline bilgileri
  const timelinePhases = [
    { 
      name: 'Awareness Phase', 
      date: 'June 1-20', 
      platforms: ['meta', 'tiktok', 'youtube'],
      description: 'Build initial awareness with high-reach formats',
      color: 'bg-gradient-to-r from-[#9A80CA] to-[#bba6e6]'
    },
    { 
      name: 'Consideration Phase', 
      date: 'June 21 - July 20', 
      platforms: ['meta', 'google', 'youtube', 'dv360'],
      description: 'Drive deeper engagement with product features',
      color: 'bg-gradient-to-r from-[#bba6e6] to-[#4F8CFF]'
    },
    { 
      name: 'Conversion Focus', 
      date: 'July 21 - August 20', 
      platforms: ['meta', 'google', 'dv360'],
      description: 'Optimize for conversions and sales',
      color: 'bg-gradient-to-r from-[#4F8CFF] to-[#9A80CA]'
    },
    { 
      name: 'Sustain Phase', 
      date: 'August 21-31', 
      platforms: ['meta', 'google', 'dv360', 'pinterest'],
      description: 'Maintain visibility and capture late-funnel conversions',
      color: 'bg-gradient-to-r from-[#ede7f6] to-[#bba6e6]'
    }
  ];

  return (
    <div className="p-6">
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden mb-6">
        <div className="p-6 border-b relative overflow-hidden">
          <div className="absolute top-0 right-0 w-1/3 h-full bg-gradient-to-l from-blue-50 to-transparent"></div>
          <div className="relative z-10">
            <h1 className="text-2xl font-bold text-gray-900">{campaignInfo.name}</h1>
            <div className="flex items-center mt-2 text-gray-500">
              <Calendar className="w-4 h-4 mr-2" />
              <span>{campaignInfo.dateRange}</span>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
              {mainKPIs.map((kpi, index) => (
                <div key={index} className={`${kpi.color} rounded-xl p-4 flex items-start justify-between`}>
                  <div>
                    <p className="text-sm font-medium">{kpi.name}</p>
                    <p className="text-2xl font-bold mt-1">{kpi.value}</p>
                  </div>
                  <div className="p-2 rounded-full bg-white/80 shadow-sm">
                    {React.cloneElement(kpi.icon, { className: "w-5 h-5" })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        
        {/* Bütçe Özeti */}
        <div className="p-6 border-b">
          <h2 className="text-lg font-semibold mb-4 flex items-center">
            <BarChart className="w-5 h-5 mr-2" />
            Budget Allocation by Platform
          </h2>
          
          <div className="flex items-center gap-4 mb-4">
            <div className="h-8 flex-grow bg-gray-100 rounded-full overflow-hidden">
              {platforms.filter(p => p.budget > 0).map((platform, index) => (
                <div 
                  key={index}
                  className={`h-full bg-gradient-to-r ${platform.color}`}
                  style={{ width: `${(platform.budget / campaignInfo.totalBudget) * 100}%` }}
                  title={`${platform.name}: ${((platform.budget / campaignInfo.totalBudget) * 100).toFixed(1)}%`}
                ></div>
              ))}
            </div>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {platforms.map((platform, index) => (
              <div key={index} className={`border ${platform.borderColor} ${platform.bgColor} rounded-lg p-3`}>
                <div className="flex items-center gap-2 mb-2">
                  <div className={`h-3 w-3 rounded-full bg-gradient-to-r ${platform.color}`}></div>
                  <span className="text-sm font-medium">{platform.name}</span>
                </div>
                <div className="text-sm">
                  <span className="font-semibold">₺{platform.budget.toLocaleString()}</span>
                  <span className="text-gray-500 text-xs"> ({((platform.budget / campaignInfo.totalBudget) * 100).toFixed(1)}%)</span>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Timeline */}
        <div className="p-6 border-b">
          <h2 className="text-lg font-semibold mb-4 flex items-center">
            <Calendar className="w-5 h-5 mr-2" />
            Campaign Timeline
          </h2>
          
          <div className="relative">
            <div className="absolute top-4 left-6 right-6 h-2 bg-gray-200 rounded-full z-0"></div>
            
            <div className="relative z-10 grid grid-cols-4 gap-4">
              {timelinePhases.map((phase, index) => (
                <div key={index} className="flex flex-col items-center">
                  <div className={`w-4 h-4 rounded-full border-2 border-white ${phase.color}`}></div>
                  <div className="mt-4 text-center">
                    <div className="text-xs text-gray-500">{phase.date}</div>
                    <div className="font-medium text-sm mt-1">{phase.name}</div>
                    <div className="mt-2 flex flex-wrap justify-center gap-1">
                      {phase.platforms.map(id => {
                        const platform = platforms.find(p => p.id === id);
                        return platform ? (
                          <div 
                            key={id} 
                            className={`h-2 w-2 rounded-full bg-gradient-to-r ${platform.color}`}
                            title={platform.name}
                          ></div>
                        ) : null;
                      })}
                    </div>
                    <div className="text-xs text-gray-500 mt-2" style={{ minHeight: '2rem' }}>
                      {phase.description}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Objectives & Platforms Matrix */}
        <div className="p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center">
            <Target className="w-5 h-5 mr-2" />
            Campaign Objectives & Platforms
          </h2>
          
          <div className="space-y-4">
            {objectives.map((objective) => (
              <div 
                key={objective.id} 
                className={`border rounded-xl ${objective.color} overflow-hidden`}
              >
                <div 
                  className="p-4 flex items-center justify-between cursor-pointer hover:bg-opacity-80"
                  onClick={() => toggleObjective(objective.id)}
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-white rounded-lg shadow-sm">
                      {objective.icon}
                    </div>
                    <div>
                      <h3 className="font-medium">{objective.name}</h3>
                      <p className="text-sm opacity-80">{objective.description}</p>
                    </div>
                  </div>
                  <div>
                    {expandedObjectives.includes(objective.id) ? 
                      <ChevronDown className="w-5 h-5" /> : 
                      <ChevronRight className="w-5 h-5" />
                    }
                  </div>
                </div>
                
                {expandedObjectives.includes(objective.id) && (
                  <div className="bg-white border-t p-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <h4 className="text-sm font-medium mb-2">Key Performance Indicators</h4>
                        <p className="text-sm">{objective.kpi}</p>
                      </div>
                      
                      <div>
                        <h4 className="text-sm font-medium mb-2">Recommended Platforms</h4>
                        <div className="flex flex-wrap gap-2">
                          {objective.platforms.map(id => {
                            const platform = platforms.find(p => p.id === id);
                            return platform ? (
                              <div key={id} className={`px-3 py-1 text-xs rounded-full flex items-center ${platform.bgColor} border ${platform.borderColor}`}>
                                <div className={`w-2 h-2 rounded-full bg-gradient-to-r ${platform.color} mr-1`}></div>
                                {platform.name}
                              </div>
                            ) : null;
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Platform Detail Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {platforms.map((platform) => (
          <div 
            key={platform.id} 
            className={`bg-white rounded-xl shadow-sm border ${platform.borderColor} overflow-hidden`}
          >
            <div className={`bg-gradient-to-r from-[#9A80CA] to-[#bba6e6] p-4 text-white`}>
              <div className="flex justify-between items-center">
                <h3 className="font-bold">{platform.name}</h3>
                <div className="text-sm opacity-80">
                  Budget: ₺{platform.budget.toLocaleString()}
                </div>
              </div>
            </div>
            
            <div className="p-4">
              <div 
                className="flex items-center justify-between cursor-pointer"
                onClick={() => togglePlatform(platform.id)}
              >
                <h4 className="font-medium">Ad Formats & Audiences</h4>
                {expandedPlatforms.includes(platform.id) ? 
                  <ChevronDown className="w-5 h-5" /> : 
                  <ChevronRight className="w-5 h-5" />
                }
              </div>
              
              {expandedPlatforms.includes(platform.id) && (
                <div className="mt-4 grid md:grid-cols-2 gap-4">
                  <div>
                    <h5 className="text-sm font-medium mb-2">Ad Formats</h5>
                    <div className="space-y-2">
                      {platform.formats.map(format => (
                        <div key={format.id} className="flex items-center text-sm">
                          <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center mr-2">
                            {format.icon}
                          </div>
                          {format.name}
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <h5 className="text-sm font-medium mb-2">Audience Targeting</h5>
                    <div className="space-y-2">
                      {platform.audiences.map(audience => (
                        <div key={audience.id} className="flex items-center text-sm">
                          <div className="w-2 h-2 rounded-full bg-gray-400 mr-2"></div>
                          {audience.name}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              
              <div className="mt-4 grid grid-cols-4 gap-2 text-center">
                <div className={`p-3 rounded-lg ${platform.bgColor}`}>
                  <div className="text-xs text-gray-500">Reach</div>
                  <div className="font-medium">{(platform.reach/1000000).toFixed(1)}M</div>
                </div>
                <div className={`p-3 rounded-lg ${platform.bgColor}`}>
                  <div className="text-xs text-gray-500">Frequency</div>
                  <div className="font-medium">{platform.frequency.toFixed(1)}x</div>
                </div>
                <div className={`p-3 rounded-lg ${platform.bgColor}`}>
                  <div className="text-xs text-gray-500">CPM</div>
                  <div className="font-medium">₺{platform.cpm.toFixed(2)}</div>
                </div>
                <div className={`p-3 rounded-lg ${platform.bgColor}`}>
                  <div className="text-xs text-gray-500">Video Views</div>
                  <div className="font-medium">{platform.videoViews > 0 ? (platform.videoViews/1000).toFixed(0) + 'K' : '-'}</div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {/* Recommendation Section */}
      <div className="mt-6 bg-white rounded-xl shadow-sm border p-6">
        <h2 className="text-lg font-semibold mb-4 flex items-center">
          <Zap className="w-5 h-5 mr-2" />
          Optimization Recommendations
        </h2>
        
        <div className="space-y-4">
          <div className="flex items-start gap-3 p-4 border rounded-lg bg-blue-50 border-blue-100">
            <div className="p-2 bg-blue-100 rounded-full text-blue-700">
              <TrendingUp className="w-4 h-4" />
            </div>
            <div>
              <h3 className="font-medium text-blue-800">Meta Ads: Increase Video Views Budget</h3>
              <p className="text-sm text-blue-700 mt-1">
                Based on performance data, allocating more budget to Meta Video Views campaigns could improve overall 
                campaign efficiency. Current CPV of ₺0.25 is 50% below benchmark.
              </p>
            </div>
          </div>
          
          <div className="flex items-start gap-3 p-4 border rounded-lg bg-green-50 border-green-100">
            <div className="p-2 bg-green-100 rounded-full text-green-700">
              <Users className="w-4 h-4" />
            </div>
            <div>
              <h3 className="font-medium text-green-800">Audience Expansion: TikTok</h3>
              <p className="text-sm text-green-700 mt-1">
                TikTok engagement rates are exceeding expectations. Consider expanding audience targeting 
                by testing new interest categories to reach wider potential customers.
              </p>
            </div>
          </div>
          
          <div className="flex items-start gap-3 p-4 border rounded-lg bg-purple-50 border-purple-100">
            <div className="p-2 bg-purple-100 rounded-full text-purple-700">
              <Filter className="w-4 h-4" />
            </div>
            <div>
              <h3 className="font-medium text-purple-800">DV360: Refine Custom Intent Targeting</h3>
              <p className="text-sm text-purple-700 mt-1">
                Add more specific keywords to Custom Intent audiences in DV360 to improve targeting precision. 
                Focus on high-converting search terms from Google Ads campaigns.
              </p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Footer Actions */}
      <div className="mt-6 flex justify-between items-center">
        <div className="text-sm text-gray-500">
          Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
        </div>
        
        <div className="flex gap-3">
          <button className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-xl text-gray-700 hover:bg-gray-50">
            <Edit3 className="w-4 h-4" />
            <span>Edit Plan</span>
          </button>
          <button className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700">
            <Download className="w-4 h-4" />
            <span>Export Plan</span>
          </button>
        </div>
      </div>
    </div>
  );
}

// Eksik bir bileşen olduğu için ekliyorum
const Eye = ({ className }) => {
  return (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      width="24" 
      height="24" 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round" 
      className={className}
    >
      <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"></path>
      <circle cx="12" cy="12" r="3"></circle>
    </svg>
  );
}; 